/**
 * Image attachment limits shared by the inbox composer, the Contact modal and
 * the Summon Admin wizard. They mirror the backend's message upload rules;
 * change them together with the backend, never per surface.
 */

export const MAX_IMAGES_PER_MESSAGE = 3;
export const MAX_IMAGES_PER_CONVERSATION = 20;
export const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
/** Value for an <input type="file" accept>. */
export const ACCEPTED_IMAGE_ACCEPT_ATTR = ACCEPTED_IMAGE_TYPES.join(',');

export interface ImageValidationResult {
  accepted: File[];
  /** Human-readable, one per rejected file or cap, in the order they occurred. */
  errors: string[];
  /** Set when the cap stopped the loop before every file was considered. */
  capHit: boolean;
}

/**
 * Validate files against the per-message cap, the accepted types and the size
 * limit. `remainingSlots` is how many more images the surface can take right
 * now (per-message cap minus what is already pending, further reduced by a
 * per-conversation cap where one applies).
 */
export const validateImageFiles = (files: File[], remainingSlots: number): ImageValidationResult => {
  const accepted: File[] = [];
  const errors: string[] = [];
  let capHit = false;

  for (const file of files) {
    if (accepted.length >= remainingSlots) {
      capHit = true;
      break;
    }
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      errors.push(`"${file.name}" is not supported. Only JPEG, PNG, and WEBP images are allowed.`);
      continue;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      errors.push(`"${file.name}" is too large. Maximum file size is 5MB.`);
      continue;
    }
    accepted.push(file);
  }

  return { accepted, errors, capHit };
};
